import {
  AdminCreateEventRequest,
  AdminUpdateEventRequest,
  deriveEventPlainDescription,
  EventExtendedViewRow,
  TicketItem,
} from 'cb-model';
import { DEFAULT_FEEDBACK_FORM_ID, DEFAULT_TICKET_TITLE } from 'cb-unified';

const parseRepetition = (value: unknown) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const parseTicketData = (ticketData: unknown): TicketItem[] => {
  if (Array.isArray(ticketData)) {
    return ticketData as TicketItem[];
  }
  if (typeof ticketData === 'string') {
    try {
      const parsed = JSON.parse(ticketData);
      return Array.isArray(parsed) ? (parsed as TicketItem[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const defaultEventDetails = (): AdminCreateEventRequest => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const startDate = new Date(`${tomorrow.toISOString().substring(0, 10)}T09:00:00`);
  const endDate = new Date(startDate.getTime() + 3600 * 1000);
  return {
    title: '',
    image: '',
    markdown_description: '',
    plain_description: '',
    type: '',
    event_profile_id: null,
    feedback_form_id: DEFAULT_FEEDBACK_FORM_ID,
    flow_option: '',
    main_host_user_id: '',
    location_type: '',
    location: '',
    gps_location: null,
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
    booking_method: 'careerbook',
    booking_url: '',
    tickets: [
      {
        id: -1,
        title: DEFAULT_TICKET_TITLE,
        price: 0,
        quantity: 10,
      },
    ],
    specialty_group: null,
    specialties: '',
    employment_grade: null,
    year_group: null,
  };
};

export const convertEventToCreateRequest = (event: EventExtendedViewRow): AdminCreateEventRequest => {
  const ticketData = parseTicketData(event.ticket_data);
  const tickets = ticketData.map((ticket, index) => ({
    id: ticket.id || -(index + 1),
    title: ticket.title,
    price: ticket.price,
    quantity: ticket.quantity,
  }));

  return {
    title: event.title,
    event_profile_id: event.event_profile_id,
    image: event.images,
    // description: event.description,
    markdown_description: event.markdown_description || '',
    plain_description: deriveEventPlainDescription(event),
    type: event.type,
    flow_option: event.flow_option || 'none',
    feedback_form_id: event.feedback_form_id,
    booking_payment_terms_id: event.booking_payment_terms_id,
    main_host_user_id: event.main_host_user_id || '',
    location_type: event.location_type,
    location: event.location || '',
    gps_location: event.gps_location,
    start_date: event.start_at,
    end_date: event.end_at,
    booking_method: event.booking_method,
    booking_url: event.booking_url || '',
    tickets: tickets.length ? tickets : defaultEventDetails().tickets,
    specialty_group: event.specialty_group,
    specialties: event.specialties,
    employment_grade: event.employment_grade,
    year_group: event.year_group,
    repetition: parseRepetition(event.repeat_pattern) || undefined,
  };
};

export const convertEventToCopyRequest = (event: EventExtendedViewRow): AdminCreateEventRequest => {
  const converted = convertEventToCreateRequest(event);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const startDate = new Date(`${tomorrow.toISOString().substring(0, 10)}T09:00:00`);
  const endDate = new Date(startDate.getTime() + 3600 * 1000);
  return {
    ...converted,
    title: `Copy of ${event.title}`,
    start_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
    repetition: undefined,
  };
};

export const asUpdateRequest = (eventId: number, createReq: AdminCreateEventRequest): AdminUpdateEventRequest => ({
  ...(createReq as AdminUpdateEventRequest),
  event_id: eventId,
});
